"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import Header from "../components/Header";

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMessage(data.message || "Thank you! We'll be in touch soon.");
        if (data.entry) {
          setFormData({ name: "", email: "" });
        }
      } else {
        setStatus("error");
        setMessage(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setMessage("Failed to submit. Please try again.");
    }
  };

  return (
    <main className="min-h-screen bg-neutral-bg dark:bg-dark-bg">
      <Header />
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Join the Brancr Waitlist
            </h1>
            <p className="text-lg text-gray-600 dark:text-dark-text-secondary">
              Be among the first to experience AI-powered marketing for your
              business.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white dark:bg-dark-surface rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-dark-border"
          >
            {status === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-600 dark:text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">
                  You&apos;re on the list!
                </h2>
                <p className="text-gray-600 dark:text-dark-text-secondary mb-6">{message}</p>
                <Link
                  href="/"
                  className="inline-block bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-colors dark:bg-dark-accent-primary dark:text-white dark:hover:bg-[#6BB8FF]"
                >
                  Back to Home
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border dark:bg-dark-elevated dark:text-dark-text-primary rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all dark:placeholder:text-dark-text-secondary"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border dark:bg-dark-elevated dark:text-dark-text-primary rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all dark:placeholder:text-dark-text-secondary"
                    placeholder="john@example.com"
                  />
                </div>

                {message && (
                  <div
                    className={`p-4 rounded-lg ${status === "error"
                        ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                        : "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                      }`}
                  >
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg dark:bg-dark-accent-primary dark:text-white dark:hover:bg-[#6BB8FF]"
                >
                  {status === "loading" ? "Submitting..." : "Join Waitlist"}
                </button>

                <p className="text-xs text-gray-500 dark:text-dark-text-secondary text-center">
                  No credit card required • We&apos;ll notify you when Brancr is
                  ready
                </p>
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 text-center"
          >
            <Link
              href="/"
              className="text-gray-600 dark:text-dark-text-secondary hover:text-primary dark:hover:text-dark-accent-primary transition-colors"
            >
              ← Back to Home
            </Link>
          </motion.div>
        </div>
      </div>
    </main>
  );
}

